import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../api/api";
import LinearProgress from "@mui/material/LinearProgress";
import {
  IAuthContext,
  IAuthContextState,
  ILoginResponse,
} from "./AuthProvider.types";
import { defaultAuthState, defaultDecodedJWTToken } from "./AuthProvider.state";
import {
  AUTH_STORAGE_KEY,
  decodeJWTToken,
  getUserStateFromLocalStorage,
  saveUserStateInLocalStorage,
} from "./AuthProvider.utils";
import { createUserStatefromResponse, getWhatsappServiceStatus, updateLocalStoreFromExternalUserSync, updateLocalStoreFromUserSync } from "./AuthProvider.helpers";
import { fetchAuthUserProfileAsync } from "./AuthProvider.services";
import { ICompanyOutlet } from "../../redux/companyOutlet/companyOutlet.types";
import { disablePushSilently, preservePushOptOut } from "../../helpers/webPush";

const AuthContext = React.createContext<IAuthContext>({
  ...defaultAuthState,
  login: () => { },
  signup: () => { },
  resendOTP: () => { },
  verifyOTP: () => { },
  forgotPassword: () => { },
  setNewPassword: () => { },
  logout: () => { },
  logoutFromAllDevices: () => { },
  addUserName: () => { },
  otherUserLogin: () => { },
  updateUserData: () => { },
  saveSignupData: () => { },
  checkUserSession: () => { },
  getActiveAuth: () => defaultAuthState,
  isExternalLogin: false,
  whatsappService: {
    isMultiple: false,
    serviceProviders: [],
    selectedService: null,
    whatsappNumber: "",
  },
  fetchUserProfile: () => { }
});

export const useAuth = () => React.useContext(AuthContext);

let globalUpdateUserData: ((userData: any) => void) | null = null;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  const [authState, setAuthState] = useState<IAuthContextState>(defaultAuthState);
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const checkUserSession = useCallback(async () => {
    try {
      setLoading(true);
      await fetchUserProfile()
    } catch (error) {
      console.error("Failed to load user profile:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    const localAuthState = getUserStateFromLocalStorage();
    if (localAuthState) {
      try {
        // Check if we're in an iframe (external user login context)
        const isInIframe = window.self !== window.top;

        if (isInIframe && localAuthState.external_auth) {
          // If in iframe, preserve external_auth (it will be set by otherUserLogin)
          const userProfile = await fetchAuthUserProfileAsync(
            localAuthState.external_auth.user.userUuid
          );
          setAuthState(updateLocalStoreFromExternalUserSync(
            localAuthState,
            userProfile
          ));
        } else {

          // Always fetch profile for the original user (not external)
          const userProfile = await fetchAuthUserProfileAsync(
            localAuthState.user.userUuid
          );
          const finalState = updateLocalStoreFromUserSync(
            localAuthState,
            userProfile
          );
          setAuthState({
            ...finalState,
            external_auth: null,
          });
        }

      } catch {
        // On error, check iframe context
        const isInIframe = window.self !== window.top;

        if (isInIframe) {
          setAuthState((prevState) => ({
            ...localAuthState,
            external_auth: prevState.external_auth,
          }));
        } else {
          setAuthState({
            ...localAuthState,
            external_auth: null,
          });
        }
      }
    }
  }

  useEffect(() => {
    checkUserSession();
  }, []);

  const handleLogin = async (
    emailOrUsername: string,
    password: string,
    onSuccess: (isOTPPreview: boolean, user: IAuthContext["user"]) => void,
    onError: (message: string) => void,
  ) => {
    try {
      const res = await api.post<ILoginResponse>("/authentication/login", {
        emailOrUsername: emailOrUsername,
        password: password,
      });
      const { data } = res.data;

      if (data.force_logout === true) {
        onError(res.data.message);
        localStorage.setItem(
          AUTH_STORAGE_KEY,
          JSON.stringify({
            userUuid: data.user.user_uuid,
            force_logout: "true",
          }),
        );
      }
      const newState = createUserStatefromResponse(authState, data);
      setAuthState(newState);
      setPassword(password);
      onSuccess(!newState.isLogin, newState.user);

      if (newState.isLogin) {
        saveUserStateInLocalStorage(newState);
      } else {
        setAuthState((prev) => ({
          ...prev,
          twoFactorData: {
            userFactId: data.user.user_fact_id,
          },
        }));
      }
      checkUserSession();
    } catch (error: any) {
      onError(error.response?.data?.message || "An unexpected error occurred");
    }
  };

  const saveSignupData = (
    data: any,
    onSuccess?: (isOTPPreview: boolean, user: IAuthContext["user"]) => void,
    onError?: (message: string) => void,
  ) => {
    if (data.force_logout === true) {
      onError?.(data.message);
      localStorage.setItem(
        AUTH_STORAGE_KEY,
        JSON.stringify({
          userUuid: data.user.user_uuid,
          force_logout: "true",
        }),
      );
    }

    const newUserState = createUserStatefromResponse(authState, data);

    setAuthState(newUserState);
    setPassword(password);
    onSuccess?.(!newUserState.isLogin, newUserState.user);

    if (newUserState.isLogin) {
      saveUserStateInLocalStorage(newUserState);
    } else {
      setAuthState((prev) => ({
        ...prev,
        twoFactorData: {
          userFactId: data.user.user_fact_id,
        },
      }));
    }
  };

  interface SignupData {
    email: string;
    username: string;
    user_password: string;
    first_name: string;
    last_name: string;
    company_outlet_name: string;
    status: string;
  }

  const handleSignup = async (
    payload: SignupData,
    onSuccess: () => void,
    onError: (message: string) => void,
  ) => {
    try {
      const res = await api.post<ILoginResponse>("/user/signup-user", payload);
      const { data } = res.data;

      const newUserState = createUserStatefromResponse(authState, data);
      if (res.data.data.token) {
        setAuthState(newUserState);
        saveUserStateInLocalStorage(newUserState);
        onSuccess();
        checkUserSession();
      }
    } catch (error: any) {
      onError(error.response?.data?.message || "An unexpected error occurred");
    }
  };

  const resendOTP = async () => {
    try {
      await api.post("/authentication/login", {
        email: authState.user.email,
        password: password,
      });
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleVerfiyOTP = async (
    otp: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ) => {
    try {
      const res = await api.post("/user/validate-otp-get-token", {
        user_fact_id: authState.twoFactorData.userFactId,
        otp: Number(otp),
      });
      const data = res.data;

      const userData = {
        ...authState.user,
        isLogin: true,
        tokens: data.data.access_token,
      };
      setAuthState((prevUser) => ({ ...prevUser, user: userData }));
      localStorage.setItem(
        "auth",
        JSON.stringify({ ...userData, date: new Date() }),
      );
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const handleLogout = async () => {
    // Normal logout for original user
    const auth = getUserStateFromLocalStorage();
    if (auth) {
      // Unregister push while the token is still valid, or this browser keeps
      // receiving the previous user's notifications.
      await disablePushSilently();
      try {
        await api.put("/authentication/logout", {
          user_uuid: auth.user.userUuid,
          sessionCount: auth.sessionCount,
        });
      } catch (error: any) {
        console.error("Logout API error:", error.response.data.message);
      }
    }
    preservePushOptOut(() => localStorage.clear());
    window.open("/auth/login", "_self");
  };

  const handleLogoutFromAllDevices = async () => {
    const auth = getUserStateFromLocalStorage();
    if (auth) {
      await disablePushSilently();
      try {
        await api.put("/authentication/logout", {
          user_uuid: auth.user.userUuid,
          sessionCount: 0,
        });
      } catch (error: any) {
        console.error("Logout API error:", error.response.data.message);
      }
    }
    preservePushOptOut(() => localStorage.clear());
    window.open("/auth/login", "_self");
  };

  const handleForgotPassword = async (
    email: string,
    otp_for: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ) => {
    try {
      // New API: Single endpoint for both email and mobile
      const payload = {
        emailOrMobile: email,
        action: "SEND",
      };

      await api.post("/authentication/forget-password", payload);
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const handleNewPassword = async (
    email: string,
    otp: string,
    new_password: string,
    onSuccess: () => void,
    onError: (message: string) => void,
  ) => {
    try {
      // New API: Single endpoint with emailOrMobile, otp, and password
      const payload = {
        emailOrMobile: email,
        otp: Number(otp),
        user_password: new_password,
      };

      await api.post("/authentication/forget-password", payload);
      onSuccess();
    } catch (error: any) {
      onError(error.response.data.message);
    }
  };

  const addUserName = (name: string) => {
    setAuthState((prevUser) => ({
      ...prevUser,
      user: { ...prevUser.user, name: name },
    }));
  };

  const updateUserData = React.useCallback((userData: IAuthContextState) => {
    setAuthState((prevState) => ({
      ...userData,
      external_auth: prevState.external_auth, // Preserve external_auth
    }));
  }, []);

  React.useEffect(() => {
    globalUpdateUserData = updateUserData;
  }, [updateUserData]);

  // Helper function to get the active auth state (external or original)
  const getActiveAuth = React.useCallback((): IAuthContextState => {
    return window.self !== window.top && authState.external_auth ? authState.external_auth : authState;
  }, [authState]);

  // Check if currently logged in as external user
  const isExternalLogin = React.useMemo(() => {
    return Boolean(window.self !== window.top && authState.external_auth && authState.external_auth.isLogin);
  }, [authState.external_auth]);


  const otherUserAccess = async (
    newState: IAuthContextState, // Complete user data from API
    onComplete: (isSuccess: boolean, error?: string) => void,
  ) => {
    try {
      setLoading(true);
      // Update auth state with external_auth
      const newAuthState = {
        ...authState,
        external_auth: newState,
      };

      // Update axios default headers to use external user's token
      api.defaults.headers.common["Authorization"] = `Bearer ${newState.token}`;
      saveUserStateInLocalStorage(newAuthState);
      setAuthState(newAuthState);
      onComplete(true);
      setLoading(false);
    } catch (error) {
      console.error("Error in other user login:", error);
      onComplete(false, "Failed to login as other user");
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }


  // Get the active auth state to display (external if present, otherwise original)
  const activeAuth = getActiveAuth();

  return (
    <AuthContext.Provider
      value={{
        // Use active auth for display purposes
        ...activeAuth,
        // Methods
        login: handleLogin,
        signup: handleSignup,
        verifyOTP: handleVerfiyOTP,
        forgotPassword: handleForgotPassword,
        setNewPassword: handleNewPassword,
        logout: handleLogout,
        logoutFromAllDevices: handleLogoutFromAllDevices,
        resendOTP: resendOTP,
        addUserName: addUserName,
        otherUserLogin: otherUserAccess,
        updateUserData: updateUserData,
        saveSignupData: saveSignupData,
        checkUserSession: checkUserSession,
        getActiveAuth: getActiveAuth,
        isExternalLogin: isExternalLogin,
        whatsappService: getWhatsappServiceStatus(activeAuth.userSettings),
        fetchUserProfile: fetchUserProfile
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export const handleCompanySwitchAsync = async (
  company_outlet_uuid: string,
  onSuccess: (isSuccess: boolean) => void,
  onError: (message: string) => void,
) => {
  try {
    const response = await api.post<ILoginResponse>(
      "/authentication/switch-company",
      {
        company_outlet_uuid: company_outlet_uuid,
      },
    );

    const { data } = response.data;

    console.log("handleCompanySwitchAsync response ==", {
      status: response.status,
      data: response.data.data,
    });
    if (data.token) {
      const currentAuth = getUserStateFromLocalStorage();
      if (currentAuth) {
        const jsonTokenData = decodeJWTToken(data.token);
        const finalJsonTokenData = jsonTokenData
          ? jsonTokenData
          : defaultDecodedJWTToken;

        const newAuthState: IAuthContextState = {
          ...currentAuth,
          user: {
            ...currentAuth.user,
            affilated_company_outlet_uuids: Array.isArray(data.user.affilated_company_outlet_uuids)
              ? data.user.affilated_company_outlet_uuids.filter(
                  (outlet: any): outlet is ICompanyOutlet => !!outlet?.company_outlet_uuid
                )
              : [],
          },
          token: data.token,
          company_outlet_timezone: finalJsonTokenData.company_outlet_timezone,
          sessionCount: data.sessionCount,
          force_logout: data.force_logout,
          date: new Date(),
          subscribed_plan: finalJsonTokenData.subscribed_plan
            ? finalJsonTokenData.subscribed_plan
            : null,
          external_auth: null, // Will be preserved by updateUserData
        };

        saveUserStateInLocalStorage(newAuthState);

        api.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
        // api.defaults.headers.common["auth-Token"] = data.token;

        if (globalUpdateUserData) {
          // updateUserData will preserve external_auth automatically
          globalUpdateUserData(newAuthState);
        }
      }
    }

    onSuccess(true);
  } catch (error: any) {
    console.log("handleCompanySwitchAsync error ==", error);
    onSuccess(false);
    onError(error.response?.data?.message || "Failed to switch company");
  }
};
